import React, { useCallback, memo } from 'react';
import { CheckCircle2Icon, ShieldCheckIcon, ZapIcon, BanknoteIcon, CalendarIcon, HomeIcon } from 'lucide-react';
const Badge = memo(({
  icon: Icon,
  text
}: {
  icon: React.ElementType;
  text: string;
}) => <div className="flex items-center gap-1.5 text-xs sm:text-sm text-[#6B7280]" data-id="element-1392">
      <Icon className="w-3.5 h-3.5 md:w-4 md:h-4 text-[#2B5FD9]" data-id="element-1393" />
      <span data-id="element-1394">{text}</span>
    </div>);
Badge.displayName = 'Badge';
export function NewHero() {
  const scrollToSection = useCallback((id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  }, []);
  return <section id="hero" className="bg-white flex items-center pt-6 md:pt-12 pb-8 md:pb-20" aria-label="Hero Creditual" data-id="element-1395">
      <div className="container mx-auto max-w-7xl px-4 py-4 md:py-12" data-id="element-1396">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-12 items-center" data-id="element-1397">
          {/* TEXTO */}
          <div className="text-center lg:text-left space-y-3 md:space-y-6" data-id="element-1398">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#2B5FD9]/10 rounded-full" data-id="element-1399">
              <HomeIcon className="h-3.5 w-3.5 text-[#2B5FD9]" data-id="element-1400" />
              <span className="text-xs md:text-sm font-semibold text-[#2B5FD9]" data-id="element-1401">
                Monetización inmobiliaria
              </span>
            </div>

            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#0A1628] leading-tight" data-id="element-1402">
              Convierte tu propiedad en ingreso mensual
            </h1>

            <p className="text-sm sm:text-base md:text-lg text-[#6B7280] leading-relaxed max-w-xl mx-auto lg:mx-0" data-id="element-1403">
              Recibe pagos mensuales por hasta 15 años usando el valor de tu
              vivienda, sin tener que venderla hoy.
            </p>

            <div className="flex flex-col sm:flex-row gap-2 md:gap-3 justify-center lg:justify-start pt-2 md:pt-4" data-id="element-1404">
              <button onClick={() => scrollToSection('simulador')} className="px-5 md:px-6 py-2.5 md:py-3 bg-[#2B5FD9] text-white text-sm md:text-base font-medium rounded-xl hover:bg-[#1E4BB8] transition-colors" data-id="element-1405">
                Simular mi ingreso mensual →
              </button>
              <a href="mailto:hola@creditual.com" className="px-5 md:px-6 py-2.5 md:py-3 bg-white border border-[#2B5FD9] text-[#2B5FD9] text-sm md:text-base font-medium rounded-xl hover:bg-gray-50 transition-colors text-center" data-id="element-1406">
                Hablar con un asesor
              </a>
            </div>

            <p className="text-xs text-[#6B7280]" data-id="element-1407">
              Pre-registro gratuito · Sin compromiso
            </p>

            <div className="flex flex-wrap justify-center lg:justify-start items-center gap-2 md:gap-4 pt-1" data-id="element-1408">
              <Badge icon={ShieldCheckIcon} text="Seguro y transparente" data-id="element-1409" />
              <Badge icon={ZapIcon} text="Resultados en minutos" data-id="element-1410" />
              <Badge icon={CheckCircle2Icon} text="Sin vender tu casa" data-id="element-1411" />
            </div>
          </div>

          {/* ILUSTRACIÓN */}
          <div className="hidden lg:flex items-center justify-center relative" data-id="element-1412">
            <div className="relative w-full max-w-2xl h-[600px]" data-id="element-1413">
              <div className="absolute inset-0 bg-gradient-to-r from-[#2B5FD9]/20 to-[#1E2A3A]/20 rounded-full blur-3xl" data-id="element-1414" />

              {/* Card 1: Ingreso mensual */}
              <div className="absolute top-20 left-0 w-80 bg-white rounded-2xl shadow-2xl border border-gray-200 p-6 transform -rotate-6 transition-transform duration-300 hover:rotate-0" data-id="element-1415">
                <div className="flex items-center justify-between mb-4" data-id="element-1416">
                  <span className="text-sm font-semibold text-[#0A1628]" data-id="element-1417">
                    Tu ingreso mensual
                  </span>
                  <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-bold rounded-full" data-id="element-1418">
                    Activo
                  </span>
                </div>
                <div className="space-y-3" data-id="element-1419">
                  <div className="flex justify-between text-sm" data-id="element-1420">
                    <span className="text-[#6B7280]" data-id="element-1421">Ingreso mensual:</span>
                    <span className="font-bold text-[#2B5FD9]" data-id="element-1422">$485.000</span>
                  </div>
                  <div className="flex justify-between text-sm" data-id="element-1423">
                    <span className="text-[#6B7280]" data-id="element-1424">Duración del plan:</span>
                    <span className="font-bold text-[#0A1628]" data-id="element-1425">15 años</span>
                  </div>
                  <div className="flex justify-between text-sm" data-id="element-1426">
                    <span className="text-[#6B7280]" data-id="element-1427">Tu propiedad:</span>
                    <span className="font-medium text-[#0A1628]" data-id="element-1428">
                      Tuya 100%
                    </span>
                  </div>
                </div>
              </div>

              {/* Card 2: Beneficio */}
              <div className="absolute top-32 right-12 w-72 bg-[#1E2A3A] rounded-2xl shadow-2xl p-6 transform rotate-3 transition-transform duration-300 hover:rotate-0 z-10" data-id="element-1429">
                <div className="text-center space-y-4" data-id="element-1430">
                  <div className="flex items-center justify-center" data-id="element-1431">
                    <div className="w-16 h-16 bg-[#2B5FD9]/20 rounded-full flex items-center justify-center" data-id="element-1432">
                      <BanknoteIcon className="w-10 h-10 text-[#2B5FD9]" data-id="element-1433" />
                    </div>
                  </div>
                  <h3 className="text-white font-bold text-xl" data-id="element-1434">
                    Liquidez sin vender
                  </h3>
                  <p className="text-[#2B5FD9] text-sm" data-id="element-1435">
                    Pagos heredables a tus beneficiarios
                  </p>
                  <div className="bg-white/10 rounded-xl p-3" data-id="element-1436">
                    <div className="flex items-center justify-between text-white text-sm" data-id="element-1437">
                      <span data-id="element-1438">Total a recibir</span>
                      <span className="font-bold" data-id="element-1439">≈ $87M</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}